import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function ARExperienceSection() {
  const { t } = useTranslation('common');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="ar-technology" className="py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1440px] mx-auto px-8 lg:px-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-600 px-5 py-2 rounded-full text-xs font-medium tracking-wider mb-6 whitespace-nowrap">
              {t('hero.arTechnology')}
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              {t('ar.title')}
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-10" style={{ lineHeight: '1.8' }}>
              {t('ar.subtitle')}
            </p>

            {/* Feature List */}
            <div className="space-y-4 mb-10">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-blue-600 text-sm"></i>
                </div>
                <p className="text-gray-700 text-base">Mobilyaları gerçek boyutlarıyla görüntüleyin</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-blue-600 text-sm"></i>
                </div>
                <p className="text-gray-700 text-base">Mekanınıza uyumunu anında test edin</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-blue-600 text-sm"></i>
                </div>
                <p className="text-gray-700 text-base">360° açıdan inceleme imkanı</p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => {
                  const vapiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (vapiButton) {
                    vapiButton.click();
                  }
                }}
                className="px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-medium tracking-wide hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('ar.tryNow')}
              </button>
              <button className="px-10 py-4 border-2 border-gray-900 text-gray-900 rounded-full text-sm font-medium tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer">
                {t('ar.learnMore')}
              </button>
            </motion.div>
          </motion.div>

          {/* Right Content - Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src="https://public.readdy.ai/ai/img_res/6b2945070a674d84c038351ac79ad263.jpg"
                alt="AR Uygulama"
                className="w-full max-w-md mx-auto rounded-[40px] shadow-2xl object-cover"
              />
            </div>

            {/* Floating Icons */}
            <motion.div
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-10 -left-10 w-20 h-20 bg-white shadow-lg rounded-2xl flex items-center justify-center"
            >
              <i className="ri-armchair-line text-3xl text-blue-600"></i>
            </motion.div>

            <motion.div
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              className="absolute bottom-20 -right-10 w-20 h-20 bg-white shadow-lg rounded-2xl flex items-center justify-center"
            >
              <i className="ri-table-2 text-3xl text-purple-600"></i>
            </motion.div>

            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute top-1/2 -right-5 w-16 h-16 bg-white shadow-lg rounded-2xl flex items-center justify-center"
            >
              <i className="ri-lightbulb-line text-2xl text-yellow-500"></i>
            </motion.div>

            {/* Decorative Circle */}
            <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-full blur-2xl"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
